import { useQuery } from '@tanstack/react-query';
import { usePaymentRouting } from './usePaymentRouting.ts';
import { useContributionDrives } from './useContributionDrives.ts';

/**
 * Hook to check if balance can be calculated for members
 * Balance can be calculated if:
 * 1. Payment routing is enabled AND
 * 2. At least one member has a target set OR active drives exist
 */
export const useCanCalculateBalance = () => {
  const { data: paymentRoutingSettings } = usePaymentRouting();
  const { data: drivesData } = useContributionDrives();

  const { data: canCalculate, isLoading } = useQuery({
    queryKey: ['can-calculate-balance', paymentRoutingSettings, drivesData],
    queryFn: async () => {
      if (!paymentRoutingSettings) {
        return false;
      }

      // Check if any house / subgroup has a target set
      const subgroupTargets = paymentRoutingSettings.subgroup_targets || {};
      const hasSubgroupTargets = Object.keys(subgroupTargets).length > 0 && 
        Object.values(subgroupTargets).some(target => {
          if (typeof target === 'number') return target > 0;
          if (typeof target === 'object' && target !== null) {
            return (Number(target.target_amount || 0) > 0) || (Number(target.amount || 0) > 0);
          }
          return false;
        });

      if (hasSubgroupTargets) {
        return true;
      }

      // Check if payment routing is enabled for standard groups
      if (!paymentRoutingSettings.contribution_amount) {
        return false;
      }

      // Check if any member has a target set
      const memberTargets = paymentRoutingSettings.member_targets || {};
      const hasMemberTargets = Object.keys(memberTargets).length > 0 && 
        Object.values(memberTargets).some(target => {
          if (typeof target === 'number') {
            return target > 0;
          }
          if (typeof target === 'object' && target !== null) {
            return target.target_amount > 0;
          }
          return false;
        });

      // Check if active drives exist
      const activeDrives = drivesData?.data?.filter(d => 
        ['active', 'pending'].includes(d.status) && d.amount_per_member
      ) || [];
      const hasActiveDrives = activeDrives.length > 0;

      return hasMemberTargets || hasActiveDrives;
    },
    enabled: !!paymentRoutingSettings,
  });

  return {
    canCalculateBalance: canCalculate ?? false,
    isLoading,
  };
};

